import React, { useMemo } from 'react'
import { Link, graphql } from 'gatsby'
import Helmet from 'react-helmet'
import Img from 'gatsby-image'

import { ExternalLinkIcon } from '../assets/ExternalLinkIcon'
import { Heading } from '../components/Heading'
import { Hero } from '../components/Hero'
import { Layout } from '../components/Layout'
import { PageLayout } from '../components/PageLayout'
import { Posts } from '../components/Posts'
import { SEO } from '../components/SEO'
import { getSimplifiedPosts } from '../utils/helpers'
import config from '../utils/config'
import { projects } from '../../content/data/projects'

export default function Index({ data }) {
  const latestNotes = data.latestNotes.edges
  const latestArticles = data.latestArticles.edges
  const highlights = data.highlights.edges
  const notes = useMemo(() => getSimplifiedPosts(latestNotes), [latestNotes])

  const articles = useMemo(
    () => getSimplifiedPosts(latestArticles),
    [latestArticles]
  )
  const simplifiedHighlights = useMemo(
    () => getSimplifiedPosts(highlights, { thumbnails: true }),
    [highlights]
  )

  return (
    <>
      <Helmet title={config.title} />
      <SEO />

      <PageLayout>
        <Hero title="Hey, I'm Alvri!" type="index">
          <div className="hero-wrapper">
            <div>
              <img
                src="/images/me.png"
                className="hero-image"
                alt={config.author}
                width="100%"
                height="100%"
              />
              <p className="hero-description">
                As a software engineer, I adeptly steer teams while meticulously
                analyzing client needs, thriving on fostering collaborative
                environments where innovative ideas flow freely, and driving
                creative problem-solving and continual improvement.
              </p>
              <p className="text-center hero-description">
                A significant thing is when my ability keeps collaborating with
                others to solve problems.
              </p>
              <p className="flex gap">
                <Link className="button" to="/me">
                  About Me
                </Link>
                <Link
                  className="button"
                  to="https://resume.azisalvriyanto.membasuh.com"
                  target="_blank"
                >
                  Resume <ExternalLinkIcon />
                </Link>
              </p>
            </div>
          </div>
        </Hero>

        <section className="section-index">
          <Heading
            title="Articles"
            description="Guides, references, and tutorials on programming."
          />
          <Posts data={articles} newspaper />
        </section>

        <section className="section-index">
          <Heading
            title="Notes"
            description="Personal notes about life, love, and everything else."
          />
          <Posts data={notes} newspaper />
        </section>

        <section className="section-index">
          <Heading
            title="In-Depth"
            description="Long-form tutorials on a variety of development topics."
          />
          <div className="cards">
            {simplifiedHighlights.map((post) => {
              return (
                <Link
                  to={post.slug}
                  className="card card-highlight"
                  key={`popular-${post.slug}`}
                >
                  {post.thumbnail && <Img fixed={post.thumbnail} />}
                  <div>{post.title}</div>
                </Link>
              )
            })}
          </div>
        </section>

        <section>
          <Heading
            title="Projects"
            slug="/projects"
            buttonText="All Projects"
            description="Amazing projects I've developed over the years."
          />

          <div className="cards">
            {projects
              .filter((project) => project.highlight)
              .map((project) => {
                return (
                  <div className="card" key={`hightlight-${project.slug}`}>
                    <time>{project.date}</time>
                    <a
                      href={`https://github.com/fyvri/${project.slug}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {project.name}
                    </a>
                    <p className="small">{project.tagline}</p>

                    <div className="card-links">
                      {project.writeup && (
                        <Link className="button small" to={project.writeup}>
                          Article
                        </Link>
                      )}
                      {project.url && (
                        <a
                          className="button small"
                          href={project.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Demo <ExternalLinkIcon />
                        </a>
                      )}
                      {project.docs && (
                        <a
                          className="button small"
                          href={project.docs}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Docs <ExternalLinkIcon />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
        </section>
      </PageLayout>
    </>
  )
}

Index.Layout = Layout

export const pageQuery = graphql`
  query IndexQuery {
    latestNotes: allMarkdownRemark(
      limit: 5
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        frontmatter: { template: { eq: "post" }, categories: { eq: "Note" } }
      }
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            tags
            categories
          }
        }
      }
    }
    latestArticles: allMarkdownRemark(
      limit: 5
      sort: { fields: [frontmatter___date], order: DESC }
      filter: {
        frontmatter: { template: { eq: "post" }, categories: { eq: "Article" } }
      }
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            tags
            categories
          }
        }
      }
    }
    highlights: allMarkdownRemark(
      limit: 12
      sort: { fields: [frontmatter___date], order: DESC }
      filter: { frontmatter: { categories: { eq: "Highlight" } } }
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            tags
            thumbnail {
              childImageSharp {
                fixed(width: 40, height: 40) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
        }
      }
    }
  }
`
